/**
 * Feature bento grid.
 *
 * Four capabilities that answer the four objections a visitor has after seeing
 * the mock: can everyone reach it, is there a fallback, is the decision visible,
 * and does anything survive the decision. The grid is irregular from `lg` up and
 * a single column on a phone, so it never becomes four small cards in a row.
 */

import { CalendarCheck, GitCompareArrows, ListOrdered, Map } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export function LandingFeatures() {
  const { t } = useTranslation()

  return (
    <section className="landing-section" aria-labelledby="landing-features-title">
      <div className="landing-container flex flex-col gap-5">
        <h2
          id="landing-features-title"
          className="max-w-3xl text-2xl font-semibold tracking-tight text-balance sm:text-3xl"
        >
          {t('home.featureGridTitle')}
        </h2>

        {/*
          The icon shares its line with the card title instead of owning a block
          above it. Stacked on a phone, four cards each paid an icon block plus a
          gap for something the heading beside it already says.
        */}
        <div className="grid gap-2.5 lg:grid-cols-3 lg:grid-rows-2">
          <article className="border-border bg-card flex flex-col gap-2 rounded-2xl border p-3.5 lg:col-span-2">
            <div className="flex items-center gap-2.5">
              <span className="bg-accent text-accent-foreground flex size-9 shrink-0 items-center justify-center rounded-xl">
                <Map className="size-4" aria-hidden />
              </span>
              <h3 className="text-base font-semibold">{t('home.featureReachTitle')}</h3>
            </div>
            <p className="text-muted-foreground max-w-xl text-sm">{t('home.featureReachBody')}</p>
          </article>

          <article className="border-border bg-card flex flex-col gap-2 rounded-2xl border p-3.5 lg:row-span-2">
            <div className="flex items-center gap-2.5">
              <span className="bg-accent text-accent-foreground flex size-9 shrink-0 items-center justify-center rounded-xl">
                <GitCompareArrows className="size-4" aria-hidden />
              </span>
              <h3 className="text-base font-semibold">{t('home.featureOptionsTitle')}</h3>
            </div>
            <p className="text-muted-foreground text-sm">{t('home.featureOptionsBody')}</p>
          </article>

          <article className="border-border bg-card flex flex-col gap-2 rounded-2xl border p-3.5">
            <div className="flex items-center gap-2.5">
              <span className="bg-accent text-accent-foreground flex size-9 shrink-0 items-center justify-center rounded-xl">
                <ListOrdered className="size-4" aria-hidden />
              </span>
              <h3 className="text-base font-semibold">{t('home.featureTransparentTitle')}</h3>
            </div>
            <p className="text-muted-foreground text-sm">{t('home.featureTransparentBody')}</p>
          </article>

          <article className="border-border bg-card flex flex-col gap-2 rounded-2xl border p-3.5">
            <div className="flex items-center gap-2.5">
              <span className="bg-accent text-accent-foreground flex size-9 shrink-0 items-center justify-center rounded-xl">
                <CalendarCheck className="size-4" aria-hidden />
              </span>
              <h3 className="text-base font-semibold">{t('home.featureKeepTitle')}</h3>
            </div>
            <p className="text-muted-foreground text-sm">{t('home.featureKeepBody')}</p>
          </article>
        </div>
      </div>
    </section>
  )
}
